import Scrollbar from '@components/custom-scroll/Scrollbar';
import ScrollbarTranscript from '@components/custom-scroll/ScrollbarTranscript';
import { TextSelectionSearchWrapper } from '@components/common/TextSelectionSearchPopover';
import ErrorBoundary from '@components/ErrorBoundary';
import Spinner from '@components/spinner/Spinner';
import { useFetchTranscriptList } from '@hooks/getQueries';
import { Grid, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import TranscriptDetail from './TranscriptDetail';
import TranscriptList from './TranscriptList';
import TranscriptWorkflowModal from './components/TranscriptWorkflowModal';

const useStyles = makeStyles(() => ({
  sidebarContainer: {
    height: '100%',
    position: 'relative',
    backgroundColor: '#ffff',
  },
  sidebar: {
    height: '100%',
    backgroundColor: '#ffff',
    borderRadius: '0 !important',
    boxShadow: 'none !important',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  stickyButton: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    borderTop: '1px solid #e8e8e8',
    padding: '16px 12px',
    marginTop: 'auto',
    marginLeft: '-12px',
    marginRight: '-12px',
    width: 'auto',
  },
}));

const Transcripts = () => {
  const classes = useStyles();
  const { transcriptId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: transcripts } = useFetchTranscriptList(id);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  const [workflowDefaultTranscriptId, setWorkflowDefaultTranscriptId] = useState(undefined);
  const workflowParam = searchParams.get('workflow');
  const workflowTranscriptId = searchParams.get('workflow_transcript_id');

  useEffect(() => {
    if (transcriptId || workflowParam !== 'open') return;

    setWorkflowDefaultTranscriptId(workflowTranscriptId || undefined);
    setIsWorkflowModalOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('workflow');
    nextParams.delete('workflow_transcript_id');
    const nextSearch = nextParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, navigate, searchParams, transcriptId, workflowParam]);

  return (
    <Grid
      container
      sx={{
        height: 'calc(100vh - 125px)',
        overflow: 'hidden',
      }}
    >
      {!transcriptId && (
        <TranscriptWorkflowModal
          open={isWorkflowModalOpen}
          onClose={() => {
            setIsWorkflowModalOpen(false);
            setWorkflowDefaultTranscriptId(undefined);
          }}
          studyId={id}
          defaultTranscriptId={workflowDefaultTranscriptId}
        />
      )}
      {/* Transcript list */}
      <Grid
        item
        xs={12}
        md={12}
        lg={2.5}
        sx={{
          height: '100%',
          borderRight: '1px solid #e8e8e8',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ minHeight: 0 }}>
            <ScrollbarTranscript className={classes.sidebarContainer}>
              <Paper
                className={classes.sidebar}
                sx={{ height: '100%', boxShadow: 'none !important', background: '#fff' }}
              >
                <ErrorBoundary>
                  <Suspense fallback={<Spinner />}>
                    <TranscriptList />
                  </Suspense>
                </ErrorBoundary>
              </Paper>
            </ScrollbarTranscript>
          </Box>
        </Box>
      </Grid>

      {/* Main content */}
      <Grid
        item
        xs={12}
        md={12}
        lg={9.5}
        sx={{
          height: '100%',
          overflow: 'auto',
        }}
      >
        <Grid container>
          {/* Main Content */}
          <Grid item xs={12} md={12}>
            <Box p={5}>
              <ErrorBoundary>
                {transcriptId ? (
                  <Scrollbar>
                    <Suspense fallback={<Spinner />}>
                      <TextSelectionSearchWrapper>
                        <TranscriptDetail paginationTranscripts={{}} transcripts={transcripts} />
                      </TextSelectionSearchWrapper>
                    </Suspense>
                  </Scrollbar>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '400px',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Select a transcript to view details
                    </Typography>
                  </Box>
                )}
              </ErrorBoundary>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Transcripts;
